"use client"

import React, { useState, useRef, useEffect, useMemo } from "react"
import { SellerSelector } from "components/selectors"
import { useLocation, useNavigate } from "react-router-dom";
import { sidebarNavLinks } from "constants/sidebarLinks/sidebarLinks";
import { useTranslation } from "react-i18next";
import DropdownNotifications from "components/Notifications/DropdownNotifications/DropdownNotifications";
import MiniMenu from "components/miniMenu/miniMenu";
import { useAppSelector } from "redux/hooks";
import { ReactComponent as LogoIcon } from "assets/icons/new/Corp/Logo.svg";
import { ReactComponent as HelpIcon } from "assets/icons/new/Profile/Help.svg";
import { ReactComponent as CopyIcon } from "assets/icons/new/UI/CopyValue.svg";
import { ReactComponent as MenuIcon } from "assets/icons/new/UI/Menu.svg";
import { ReactComponent as HideMenuIcon } from "assets/icons/new/UI/HideMenu.svg";

const Header = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const [shouldTruncate, setShouldTruncate] = useState(false)
  const headerRef = useRef(null)
  const leftSectionRef = useRef(null)
  const rightSectionRef = useRef(null)
  const { t } = useTranslation();

  const { currentSeller } = useAppSelector(({ sellers }) => ({ currentSeller: sellers.currentSeller }))

  useEffect(() => {
    const checkOverflow = () => {
      if (headerRef.current && leftSectionRef.current && rightSectionRef.current) {
        const headerWidth = headerRef.current.offsetWidth
        const leftWidth = leftSectionRef.current.offsetWidth
        const rightWidth = rightSectionRef.current.offsetWidth
        const padding = 32 // 16px с каждой стороны

        const totalRequiredWidth = leftWidth + rightWidth + padding
        setShouldTruncate(totalRequiredWidth > headerWidth)
      }
    }

    const timeoutId = setTimeout(checkOverflow, 100)

    window.addEventListener("resize", checkOverflow)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("resize", checkOverflow)
    }
  }, [])
  // Вкладки раздела «Остатки» — обрабатываются отдельно
  const stocksSubTabs = {
    '/stocks-fbs':    'FBS',
    '/stocks-fbo':    'FBO',
    '/stocks-export': 'Отправка',
    '/stocks-reserve':'Резервы',
  }

  const breadcrumbs = useMemo(() => {
    if (!currentSeller?.info?.data?.id) return []

    const cleanPath = pathname.replace('/' + currentSeller.info.data.id, '')
    const firstSegment = '/' + cleanPath.split('/').filter(Boolean)[0]
    const subTabLabel = stocksSubTabs[cleanPath] || stocksSubTabs[firstSegment]

    const breadcrumbs = [{ name: 'home', path: '/' }]

    if (subTabLabel) {
      // Все 5 вкладок Остатков: home / Остатки / [вкладка]
      breadcrumbs.push({ name: 'remains', path: '/stocks-fbs' })
      breadcrumbs.push({ name: subTabLabel, path: cleanPath })
    } else {
      const pathSegments = cleanPath.split('/').filter(Boolean)
      pathSegments.forEach((segment, index) => {
        const path = '/' + pathSegments.slice(0, index + 1).join('/')
        const navItem = sidebarNavLinks.find((item) => item.path === path)
        if (navItem) {
          breadcrumbs.push({ name: navItem.name, path })
        }
      })
    }

    return breadcrumbs
  }, [pathname, currentSeller])

  const copyCompanyId = async () => {
    try {
      await navigator.clipboard.writeText(currentSeller?.info?.data?.id)
      console.log('copied')
    } catch (err) {
      console.error("Ошибка копирования:", err)
    }
  }

  const clickBurger = (e) => {
    setSidebarOpen(old => !old)
  }

  return (
    <header
      id='appHeader'
      ref={headerRef}
      className={`sm:fixed lg:fixed max-sm:absolute max-sm:h-8.5 z-100 top-0 flex z-62 items-center bg-theme-bg dark:bg-theme-bg w-full lg:py-1.5 text-theme-color dark:text-theme-color font-sm lg:pr-2.5 maxh620:p-0 max-sm:pr-2.5`}
    >
      {/* Левая секция */}
      <div ref={leftSectionRef} className="flex items-center flex-shrink-0 z-20">
        <button
          onClick={clickBurger}
          id="burgerMenuBtnSm"
          className={`items-center w-10.5 justify-center flex max-sm:hidden hover:text-theme-hover dark:hover:text-theme-hover`}
        >
          {
            !sidebarOpen
            ? <MenuIcon width="20" height="20" fill="currentColor" />
            : <HideMenuIcon width="20" height="20" fill="currentColor" />
          }
        </button>

        <div className={`flex items-center gap-x-[2px] max-sm:ml-[12px] text-sm cursor-copy hover:text-theme-hover dark:hover:text-theme-hover`} onClick={copyCompanyId}>
          <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-28">{currentSeller?.info?.data?.id}</span>
          <button
            className="h-6 w-6 flex items-center justify-center"
            title="Копировать ID"
          >
            <CopyIcon width="14" height="14" fill="currentColor" />
          </button>
        </div>

        <SellerSelector shouldTruncate={shouldTruncate} className="ml-[14px]"/>
      </div>

      {/* Правая секция */}
      <div
        ref={rightSectionRef}
        className={`flex items-center ml-auto z-10 lg:ml-0 sm:ml-0 md:ml-0 sm:flex-1 lg:flex-none maxh620:flex-1 space-x-2.5 min-w-0 max-sm:fixed max-sm:w-full max-sm:pr-2.5 max-sm:h-[var(--mobile-bar-h)] max-sm:pb-[env(safe-area-inset-bottom,0px)] max-sm:box-border max-sm:bg-theme-bg dark:max-sm:bg-theme-bg bottom-0`}
      >
        {/* Кнопка бургера для мобилок */}
        <button
          onClick={clickBurger}
          id="burgerMenuBtnMaxSm"
          className={`items-center ml-2.5 gap-x-2 text-sm justify-center hidden max-sm:flex hover:text-theme-hover dark:hover:text-theme-hover flex-shrink-0`}
        >
          <MenuIcon width="20" height="20" fill="currentColor" className={sidebarOpen && 'hidden'} />
          <HideMenuIcon width="20" height="20" fill="currentColor" className={!sidebarOpen && 'hidden'} />
          {t("MENU")}
        </button>

        {/* Хлебные крошки */}
        <div className="flex items-center space-x-2 text-sm min-w-0 overflow-hidden lg:flex-none shrink">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={`${crumb.path}-${index}`}>
              {index > 0 && <span className="font-thin opacity-60 flex-shrink-0">&#x2215;</span>}
              <button
                className={`transition-colors whitespace-nowrap hover:text-theme-hover dark:hover:text-theme-hover ${
                  index === breadcrumbs.length - 1
                    ? 'min-w-0 flex-shrink truncate'
                    : 'flex-shrink-0'
                }`}
                onClick={() => {
                  if (index < breadcrumbs.length - 1) navigate(crumb.path)
                }}
              >
                {t(crumb.name)}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Знак вопроса */}
        <a
          href="https://lk.market/ru/help"
          target="_blank"
          rel="noreferrer"
          className={`flex-shrink-0 hover:text-theme-hover dark:hover:text-theme-hover`}
          title="Справка"
        >
          <HelpIcon width="20" height="20" fill="currentColor" />
        </a>

        {/* Вертикальный разделитель */}
        <div className={`border-e w-px h-6 mx-1.5 opacity-60 flex-shrink-0 border-theme-border dark:border-theme-border`}></div>

        {/* Уведомления */}
        <DropdownNotifications align="left" />

        {/* Распорка — прижимает MiniMenu вправо на планшетах и широких коротких экранах */}
        <div className="hidden sm:block lg:hidden maxh620:block max-sm:hidden flex-1" />

        {/* MiniMenu для планшетов */}
        <div className="items-center lg:hidden sm:flex maxh620:flex max-sm:hidden">
          <MiniMenu sidebarOpen={sidebarOpen} hideMdElements={true} />
        </div>
      </div>

      {/* Лого */}
      <LogoIcon className={`hidden lg:block maxh620:hidden ml-auto hover:text-theme-hover dark:hover:text-theme-hover flex-shrink-0`} height='30px' width='150px' fill="currentColor" />
      <div className="items-center hidden max-sm:flex ml-auto flex-shrink-0 max-sm:gap-x-[9px]">
        <MiniMenu sidebarOpen={sidebarOpen} hideMdElements={true} />
      </div>
    </header>
  )
}

export default Header
